/* 
Generated code: do not modify this file as it will be overwritten
Template: ImmutableJSRecord.cst

Known Issues/TODO:
- Using *Props type as Record constructor parameter is insufficient.
If a property needs to be a Record type, it should already be constructed.
- Rename Record fromJS factory method to fromJSON
- Create parseJSON method that translates JSON to Props



 */

/* eslint-disable import/order */

import { createSelector } from '@ngrx/store';
import { List, Map, Record, Seq, Set } from 'immutable';
import { NotificationGroupType } from 'src/graphql/generated/graphql.generated';
import { AppState } from 'src/store/appstate';
import { getFromOrThrow, propValueOrDefault, tryOrDefault } from 'src/util/values';

export interface NotificationGroupProps {
  id: string;
  name: string;
  companyId: string;
  unitId: string | null;
  regionId: string | null;
  type: NotificationGroupType | null;
  types: Set<NotificationGroupType> | null;
  isEmailNotificationPreferred: boolean | null;
  dateLastUpdated: Date;
  dateCreated: Date;
  dateDeleted: Date | null;
  lastUpdatedById: string | null;
}

export class NotificationGroupRecord extends Record<NotificationGroupProps>({
  id: null as any,
  name: null as any,
  companyId: null as any,
  unitId: null as any,
  regionId: null as any,
  type: null as any,
  types: null as any,
  isEmailNotificationPreferred: null as any,
  dateLastUpdated: null as any,
  dateCreated: null as any,
  dateDeleted: null as any,
  lastUpdatedById: null as any,
}) {
  public static fromJS(json: {
    id: string;
    name: string;
    companyId: string;
    unitId: string | null;
    regionId: string | null;
    type: NotificationGroupType | null;
    types: any;
    isEmailNotificationPreferred: boolean | null;
    dateLastUpdated: string | number | Date;
    dateCreated: string | number | Date;
    dateDeleted: string | number | Date | null;
    lastUpdatedById: string | null;
  }): NotificationGroupRecord {
    if (json == null) {
      throw new Error('NotificationGroupRecord.fromJS: parameter cannot be null');
    }
    return new NotificationGroupRecord({
      id: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'id', '00000000-0000-0000-0000-000000000000'),
        'NotificationGroupJson.id',
      ),
      name: getFromOrThrow(json, (j) => j.name, 'NotificationGroupJson.name'),
      companyId: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'companyId', '00000000-0000-0000-0000-000000000000'),
        'NotificationGroupJson.companyId',
      ),
      unitId: json.unitId,
      regionId: json.regionId,
      type: json.type,
      types: json.types != null ? Set<NotificationGroupType>(json.types) : null,
      isEmailNotificationPreferred: json.isEmailNotificationPreferred,
      dateLastUpdated: new Date(json.dateLastUpdated),
      dateCreated: new Date(json.dateCreated),
      dateDeleted: json.dateDeleted != null ? new Date(json.dateDeleted) : null,
      lastUpdatedById: json.lastUpdatedById,
    });
  }

  public static fromJSUnsafe(json: {
    id: string;
    name: string;
    companyId: string;
    unitId: string | null;
    regionId: string | null;
    type: NotificationGroupType | null;
    types: any;
    isEmailNotificationPreferred: boolean | null;
    dateLastUpdated: string | number | Date;
    dateCreated: string | number | Date;
    dateDeleted: string | number | Date | null;
    lastUpdatedById: string | null;
  }): [NotificationGroupRecord, Error[]] {
    if (json == null) {
      throw new Error('NotificationGroupRecord.fromJSUnsafe: parameter cannot be null');
    }
    const errors: Error[] = [];

    const record = new NotificationGroupRecord({
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      id: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'id', '00000000-0000-0000-0000-000000000000'),
            'NotificationGroupJson.id',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      name: tryOrDefault(
        () => getFromOrThrow(json, (j) => j.name, 'NotificationGroupJson.name'),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      companyId: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'companyId', '00000000-0000-0000-0000-000000000000'),
            'NotificationGroupJson.companyId',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      unitId: tryOrDefault(
        () => json.unitId,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      regionId: tryOrDefault(
        () => json.regionId,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      type: tryOrDefault(
        () => json.type,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      types: tryOrDefault(
        () => (json.types != null ? Set<NotificationGroupType>(json.types) : null),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      isEmailNotificationPreferred: tryOrDefault(
        () => json.isEmailNotificationPreferred,
        (err) => errors.push(err),
      )!,
      dateLastUpdated: new Date(json.dateLastUpdated),
      dateCreated: new Date(json.dateCreated),
      dateDeleted: json.dateDeleted != null ? new Date(json.dateDeleted) : null,
      lastUpdatedById: json.lastUpdatedById,
    });

    return [record, errors];
  }

  constructor(props: NotificationGroupProps) {
    super(props);
    if (props == null) {
      throw new Error('NotificationGroupRecord constructor parameter cannot be null');
    }
  }
}

export abstract class NotificationGroupViewModelBase implements NotificationGroupProps {
  protected readonly _props: NotificationGroupProps;
  private _json?: NotificationGroupProps;

  get id(): string {
    return this._props.id;
  }
  get name(): string {
    return this._props.name;
  }
  get companyId(): string {
    return this._props.companyId;
  }
  get unitId(): string | null {
    return this._props.unitId;
  }
  get regionId(): string | null {
    return this._props.regionId;
  }
  get type(): NotificationGroupType | null {
    return this._props.type;
  }
  get types(): Set<NotificationGroupType> | null {
    return this._props.types;
  }
  get isEmailNotificationPreferred(): boolean | null {
    return this._props.isEmailNotificationPreferred;
  }

  get dateCreated(): Date {
    return this._props.dateCreated;
  }
  get dateLastUpdated(): Date {
    return this._props.dateLastUpdated;
  }
  get dateDeleted(): Date | null {
    return this._props.dateDeleted;
  }
  get lastUpdatedById(): string | null {
    return this._props.lastUpdatedById;
  }

  public get isAviationGroup() {
    return this._props.type === NotificationGroupType.AviationGroup;
  }
  public get isClinicalGroup() {
    return this._props.type === NotificationGroupType.ClinicalGroup;
  }
  public get isMaintenanceController() {
    return this._props.type === NotificationGroupType.MaintenanceController;
  }
  public get isOccGroup() {
    return this._props.type === NotificationGroupType.OccGroup;
  }
  public get isOnlyCrew() {
    return this._props.type === NotificationGroupType.OnlyCrew;
  }
  public get isOnlyPilots() {
    return this._props.type === NotificationGroupType.OnlyPilots;
  }
  public get isOperationsGroup() {
    return this._props.type === NotificationGroupType.OperationsGroup;
  }
  public get isPaipGroup() {
    return this._props.type === NotificationGroupType.PaipGroup;
  }
  public get isSpecialityGroup() {
    return this._props.type === NotificationGroupType.SpecialityGroup;
  }

  public isAnyType(...notificationGroupType: NotificationGroupType[]) {
    return this._props.type != null && notificationGroupType.includes(this._props.type);
  }

  constructor(props: NotificationGroupProps) {
    this._props = props;
  }

  public toJS() {
    return this.toJSON();
  }

  public toJSON() {
    if (this._json == null) {
      this._json = {
        id: this._props.id,
        name: this._props.name,
        companyId: this._props.companyId,
        unitId: this._props.unitId,
        regionId: this._props.regionId,
        type: this._props.type,
        types: this._props.types,
        isEmailNotificationPreferred: this._props.isEmailNotificationPreferred,
        dateLastUpdated: this._props.dateLastUpdated,
        dateCreated: this._props.dateCreated,
        dateDeleted: this._props.dateDeleted,
        lastUpdatedById: this._props.lastUpdatedById,
      };
    }
    return this._json;
  }
}

//#region Selectors

export const selectAllNotificationGroupEntities = (state: AppState) =>
  state.entityStores.notificationGroup.entities;

export const selectNotificationGroupById = (id: string) => (state: AppState) =>
  state.entityStores.notificationGroup.entities.get(id);

export const selectNotificationGroupMetadataById = (id: string) => (state: AppState) =>
  state.entityStores.notificationGroup.meta.get(id);

export const selectNotificationGroupAndMetadataById = (id: string) =>
  createSelector(
    selectNotificationGroupById(id),
    selectNotificationGroupMetadataById(id),
    (notificationGroup, meta) => ({ notificationGroup, meta }),
  );

export function selectNotificationGroupEntitiesByIds(ids: string[]) {
  return createSelector(
    selectAllNotificationGroupEntities,

    (entities) => {
      // TODO: Once we're on TS 3.5+, I don't think we'll need use type assertions
      const entries = ids.map((id) => [id, entities.get(id)]) as unknown as Iterable<
        [string, NotificationGroupRecord | undefined]
      >;
      return Map(entries);
    },
  );
}

export function selectNotificationGroupEntitiesByName(name: string) {
  return createSelector(selectAllNotificationGroupEntities, (entities) =>
    entities.filter((e) => e.name === name).valueSeq(),
  );
}

export function selectNotificationGroupEntitiesByCompanyId(companyId: string) {
  return createSelector(selectAllNotificationGroupEntities, (entities) =>
    entities.filter((e) => e.companyId === companyId).valueSeq(),
  );
}

export function selectNotificationGroupEntitiesByUnitId(unitId: string) {
  return createSelector(selectAllNotificationGroupEntities, (entities) =>
    entities.filter((e) => e.unitId === unitId).valueSeq(),
  );
}

export function selectNotificationGroupEntitiesByRegionId(regionId: string) {
  return createSelector(selectAllNotificationGroupEntities, (entities) =>
    entities.filter((e) => e.regionId === regionId).valueSeq(),
  );
}

export function selectNotificationGroupEntitiesByType(type: NotificationGroupType) {
  return createSelector(selectAllNotificationGroupEntities, (entities) =>
    entities.filter((e) => e.type === type).valueSeq(),
  );
}

export function selectNotificationGroupEntitiesByIsEmailNotificationPreferred(
  isEmailNotificationPreferred: boolean,
) {
  return createSelector(selectAllNotificationGroupEntities, (entities) =>
    entities
      .filter((e) => e.isEmailNotificationPreferred === isEmailNotificationPreferred)
      .valueSeq(),
  );
}
//#endregion Selectors
